import { useVoucherList } from "@bundles/vouchers/data/hooks/useVoucherList"
import { Box, Button, Divider, Typography } from "@tm/components"
import { useCountryCodeToLicensePlate, useWorkTask } from "@tm/context-distribution"
import { useLocalization } from "@tm/localization"
import { VoucherType } from "@tm/models"
import { useParams } from "react-router"
import { VoucherRouteParams } from "@bundles/vouchers/business"
import { ArticleSearchFields } from "../_shared/ArticleSearchFields"
import { WidgetCostEstimationsList } from "./lists/WidgetCostEstimationsList"
import { NoResultHint } from "../_shared/NoResultHint"
import { LoaderSmall } from "../_shared/StyledComponents"
import { WidgetOrderList } from "./lists/WidgetOrderList"
import { WidgetReturnsList } from "./lists/WidgetReturnsList"

type Props = {
    voucherType: VoucherType
    showName: boolean
    url: string
    enabled: boolean
    externalSystemId?: number
    showArticleNumberSearch?: boolean
    onOpenOrderVoucher: () => void
}

export function VoucherWidgetContent(props: Props) {
    const { voucherType, showArticleNumberSearch, showName, externalSystemId, url, enabled, onOpenOrderVoucher } = props
    const { workTask } = useWorkTask() ?? {}
    const { plateCode } = useCountryCodeToLicensePlate(workTask?.vehicle?.countryCode)
    const matchParams = useParams<VoucherRouteParams>()
    const { costEstimations, costEstimationsLoading, orders, ordersLoading, returns, returnsLoading } = useVoucherList(
        voucherType,
        matchParams.workTaskId,
        workTask?.customer?.id,
        undefined,
        undefined,
        enabled
    )
    const { translateText } = useLocalization()

    function renderArticleNumberSearch() {
        return (
            <Box flex={1} mt={1} height="50%">
                <Typography variant="body3">{translateText(389)}</Typography>
                <Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
                    <ArticleSearchFields hideClearButton url={url} variant="widget" />
                </Box>
            </Box>
        )
    }

    function renderLoaderSmall() {
        return <LoaderSmall data-testid="loader-small" />
    }

    function renderNoResultHint() {
        return <NoResultHint data-testid="no-result-hint" />
    }

    function renderOpenOrderVoucher() {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
                <Button onClick={onOpenOrderVoucher} size="large">
                    {translateText(14055)}
                </Button>
            </Box>
        )
    }

    function renderList() {
        switch (voucherType) {
            case VoucherType.CostEstimation: {
                if (costEstimationsLoading) {
                    return renderLoaderSmall()
                }

                if (!costEstimations?.length) {
                    return renderNoResultHint()
                }

                return (
                    <WidgetCostEstimationsList
                        costEstimations={costEstimations}
                        shortCountryCode={plateCode}
                        showCustomerColumn={showName}
                        data-testid="widget-cost-estimations-list"
                    />
                )
            }

            case VoucherType.Order: {
                if (externalSystemId) {
                    return renderOpenOrderVoucher()
                }

                if (ordersLoading) {
                    return renderLoaderSmall()
                }

                if (!orders?.length) {
                    return renderNoResultHint()
                }

                return <WidgetOrderList orders={orders} shortCountryCode={plateCode} showCustomerColumn={showName} data-testid="widget-order-list" />
            }

            case VoucherType.Return: {
                if (returnsLoading) {
                    return renderLoaderSmall()
                }

                if (!returns?.length) {
                    return renderNoResultHint()
                }

                return <WidgetReturnsList returns={returns} data-testid="widget-returns-list" />
            }

            default: {
                return null
            }
        }
    }

    return (
        <Box height="100%">
            <Box sx={{ position: "relative", height: showArticleNumberSearch ? "50%" : "100%" }}>{renderList()}</Box>
            {showArticleNumberSearch && (
                <>
                    <Divider />
                    {renderArticleNumberSearch()}
                </>
            )}
        </Box>
    )
}
